/**
 * Custom hook for OTD Performance Settings state and API operations.
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { performanceSettingsAPI } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { PerformanceFormData } from './types';

const DEFAULT_FORM: PerformanceFormData = {
  otd_excellent_threshold: '90.00',
  otd_good_threshold: '70.00',
  otd_poor_threshold: '50.00',
};

export function usePerformanceSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState<PerformanceFormData>(DEFAULT_FORM);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await performanceSettingsAPI.getSettings();
      setFormData({
        otd_excellent_threshold: response.data.otd_excellent_threshold ?? DEFAULT_FORM.otd_excellent_threshold,
        otd_good_threshold: response.data.otd_good_threshold ?? DEFAULT_FORM.otd_good_threshold,
        otd_poor_threshold: response.data.otd_poor_threshold ?? DEFAULT_FORM.otd_poor_threshold,
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to load performance settings:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load performance settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleInputChange = useCallback((field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      await performanceSettingsAPI.updateSettings({
        otd_excellent_threshold: formData.otd_excellent_threshold,
        otd_good_threshold: formData.otd_good_threshold,
        otd_poor_threshold: formData.otd_poor_threshold,
      });
      setHasChanges(false);
      toast.success('OTD settings saved successfully');
    } catch (error: unknown) {
      console.error('Failed to save performance settings:', error);
      toast.error(getApiErrorMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  }, [formData]);

  return {
    loading,
    saving,
    hasChanges,
    formData,
    handleInputChange,
    handleSave,
    loadSettings,
  };
}
